'use client';
import React from 'react';

const PublishingSection: React.FC = () => {
  const handleScrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const publishingAreas = [
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="32" height="32" rx="4" fill="rgba(56,126,247,0.1)"/>
          <path d="M12 12h16M12 17h16M12 22h10" stroke="#387EF7" strokeWidth="2" strokeLinecap="round"/>
          <path d="M26 24l4 4m-2-6a4 4 0 11-8 0 4 4 0 018 0z" stroke="#387EF7" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: 'Magazine Publishing',
      description: 'Professional magazine publishing services focused on editorial quality, content strategy, and audience engagement across various topics and industries.',
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="32" height="32" rx="4" fill="rgba(56,126,247,0.1)"/>
          <path d="M14 10h12v20H14z" stroke="#387EF7" strokeWidth="2" strokeLinejoin="round"/>
          <path d="M17 15h6M17 19h6M17 23h4" stroke="#387EF7" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: 'Editorial Content',
      description: 'Thoughtfully crafted editorial content that informs, educates, and engages readers. We prioritize accuracy, clarity, and compelling storytelling.',
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="32" height="32" rx="4" fill="rgba(56,126,247,0.1)"/>
          <rect x="10" y="12" width="20" height="14" rx="2" stroke="#387EF7" strokeWidth="2"/>
          <path d="M14 30h12M20 26v4" stroke="#387EF7" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: 'Digital Publications',
      description: 'Modern digital publishing solutions that bring periodical content to readers on any device. Expanding reach beyond traditional print formats.',
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="32" height="32" rx="4" fill="rgba(56,126,247,0.1)"/>
          <path d="M10 20h4l3-8 4 16 3-10 3 6 3-4" stroke="#387EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Periodical Content',
      description: 'Regular, scheduled periodical publications that keep audiences informed and engaged. Consistent content delivery that builds loyal readership.',
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="32" height="32" rx="4" fill="rgba(56,126,247,0.1)"/>
          <circle cx="20" cy="18" r="6" stroke="#387EF7" strokeWidth="2"/>
          <path d="M14 30c0-3.31 2.69-6 6-6s6 2.69 6 6" stroke="#387EF7" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: 'Publishing Services',
      description: 'Comprehensive publishing support services for the periodicals industry. From content planning to final distribution, we provide professional guidance.',
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="32" height="32" rx="4" fill="rgba(56,126,247,0.1)"/>
          <path d="M12 28l4-8 4 4 4-10 4 14" stroke="#387EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="28" cy="14" r="3" stroke="#387EF7" strokeWidth="2"/>
        </svg>
      ),
      title: 'Media & Content Development',
      description: 'Strategic media and content development that aligns publishing goals with audience needs. Building content ecosystems that deliver lasting value.',
    },
  ];

  return (
    <section
      id="publishing"
      className="py-24"
      style={{ backgroundColor: '#F2F3F3' }}
      aria-label="Publishing and Periodicals services"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span
            className="inline-block text-sm uppercase tracking-widest font-medium mb-3"
            style={{ fontFamily: 'Poppins, sans-serif', color: '#0066CC', letterSpacing: '3px' }}
          >
            What We Do
          </span>
          <h2
            className="mb-6"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(28px, 3.5vw, 42px)',
              color: '#122658',
              lineHeight: 1.2,
            }}
          >
            Publishing & Periodicals
          </h2>
          <div className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: '#387EF7' }} />
          <p
            className="max-w-3xl mx-auto"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '16px',
              color: '#7F7F90',
              lineHeight: 1.8,
            }}
          >
            Caps N Corks II, LLC operates in the Periodicals industry — specifically in Magazines: Publishing Only, Not Printed On Site. Our focus is on the editorial and publishing side of the magazine industry.
          </p>
        </div>

        {/* Publishing Areas Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {publishingAreas.map((area, index) => (
            <div
              key={index}
              className="p-8 rounded-lg bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover group"
              style={{
                boxShadow: '2px 2px 5px 0px rgba(0,0,0,0.2)',
                borderRadius: '5px',
              }}
            >
              <div className="mb-5">{area.icon}</div>
              <h3
                className="mb-4"
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 700,
                  fontSize: '18px',
                  color: '#122658',
                }}
              >
                {area.title}
              </h3>
              <p
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px',
                  color: '#7F7F90',
                  lineHeight: 1.7,
                }}
              >
                {area.description}
              </p>
            </div>
          ))}
        </div>

        {/* Industry Note */}
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: '#122658' }}
        >
          <h3
            className="mb-4"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 700,
              fontSize: '24px',
              color: '#ffffff',
            }}
          >
            Magazines: Publishing Only, Not Printed On Site
          </h3>
          <p
            className="mb-8 max-w-2xl mx-auto"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '15px',
              color: 'rgba(255,255,255,0.8)',
              lineHeight: 1.8,
            }}
          >
            As a publishing-focused company, Caps N Corks II, LLC concentrates on the editorial, content, and publishing aspects of the magazine industry. We do not operate a physical printing facility — our expertise lies in the creation, curation, and distribution of quality periodical content.
          </p>
          <button
            onClick={() => handleScrollTo('contact')}
            className="inline-flex items-center justify-center px-8 py-3 text-white font-medium uppercase tracking-widest transition-all duration-200 hover:opacity-90"
            style={{
              backgroundColor: '#387EF7',
              borderRadius: '7px',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '13px',
              letterSpacing: '2px',
            }}
          >
            Send an Inquiry
          </button>
        </div>
      </div>
    </section>
  );
};

export default PublishingSection;
